import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, Plus, Search, Edit, Trash2, Copy, Send, Download, Eye,
  X, Save, Calendar, User, Building2, Package, Percent, Loader2,
  Check, CheckCircle, Clock, XCircle, AlertCircle, ChevronDown, Truck, DollarSign, Mail, FileUp, Receipt, FileSignature
} from 'lucide-react';
import { toast } from 'sonner';
import RentalQuotationsTab from './RentalQuotationsTab';
import DeliveryLetterModal from './DeliveryLetterModal';
import EmailEditModal from './EmailEditModal';
import { useBusiness } from '../contexts/BusinessContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-slate-500', icon: Edit },
  sent: { label: 'Enviada', color: 'bg-blue-500', icon: Send },
  accepted: { label: 'Aceptada', color: 'bg-green-500', icon: Check },
  rejected: { label: 'Rechazada', color: 'bg-red-500', icon: XCircle },
  expired: { label: 'Expirada', color: 'bg-orange-500', icon: AlertCircle }
};

// Format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  }).format(value || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('es-MX', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

// Quotation Item Row
const buildVehicleName = (v) => {
  const brand = (v.brand || '').toUpperCase().trim();
  const model = (v.model_name || '').toUpperCase().trim();
  const version = (v.version || '').toUpperCase().trim();
  const year = (v.year || '').toString().trim();
  const modelWords = new Set(model.split(/\s+/).filter(Boolean));
  const versionWords = new Set(version.split(/\s+/).filter(Boolean));
  const overlap = [...modelWords].filter(w => versionWords.has(w));
  let base;
  if (overlap.length >= 1 && version) {
    base = version;
  } else if (version && model) {
    base = `${model} ${version}`.trim();
  } else if (model) {
    base = model;
  } else {
    base = '';
  }
  // Remove brand from base if already present (e.g. CATERPILLAR in "CARGADOR FRONTAL CATERPILLAR")
  const baseWords = base.split(/\s+/).filter(Boolean);
  const brandWords = brand.split(/\s+/).filter(Boolean);
  const cleanedBase = baseWords
    .filter(w => !brandWords.includes(w))
    .join(' ');
  let name = brand
    ? (cleanedBase ? `${brand} ${cleanedBase}` : brand)
    : cleanedBase;
  if (year && !name.includes(year)) {
    name = `${name} ${year}`.trim();
  }
  return name.replace(/\s+/g, ' ').trim();
};

const QuotationItemRow = ({ item, index, vehicles, onUpdate, onRemove }) => {
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const handleVehicleSelect = (vehicleId) => {
    const vehicle = vehicles.find(v => v.id === vehicleId);
    if (vehicle) {
      setSelectedVehicle(vehicle);
      const price = parseFloat(vehicle.price?.replace(/[,$]/g, '') || 0);
      onUpdate(index, {
        ...item,
        vehicle_id: vehicle.id,
        code: vehicle.clv || vehicle.id.slice(0, 8).toUpperCase(),
        concept: buildVehicleName(vehicle),
        unit_price: price,
        subtotal: item.quantity * price
      });
    }
  };

  return (
    <tr className="border-b border-slate-700/50">
      <td className="py-3 pr-2">
        <select
          value={item.vehicle_id || ''}
          onChange={(e) => handleVehicleSelect(e.target.value)}
          className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:border-blue-500 focus:outline-none"
        >
          <option value="">Seleccionar equipo...</option>
          {vehicles.map(v => (
            <option key={v.id} value={v.id}>
              {v.brand} {v.model_name} {v.version}
            </option>
          ))}
        </select>
      </td>
      <td className="py-3 px-2">
        <input
          type="text"
          value={item.concept}
          onChange={(e) => onUpdate(index, { ...item, concept: e.target.value })}
          className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm focus:border-blue-500 focus:outline-none"
          placeholder="Descripción"
        />
      </td>
      <td className="py-3 px-2 w-20">
        <input
          type="number"
          min="1"
          value={item.quantity}
          onChange={(e) => {
            const qty = parseInt(e.target.value) || 1;
            onUpdate(index, { ...item, quantity: qty, subtotal: qty * item.unit_price });
          }}
          className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm text-center focus:border-blue-500 focus:outline-none"
        />
      </td>
      <td className="py-3 px-2 w-32">
        <input
          type="number"
          step="0.01"
          value={item.unit_price}
          onChange={(e) => {
            const price = parseFloat(e.target.value) || 0;
            onUpdate(index, { ...item, unit_price: price, subtotal: item.quantity * price });
          }}
          className="w-full px-2 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm text-right focus:border-blue-500 focus:outline-none"
        />
      </td>
      <td className="py-3 px-2 w-32 text-right text-white">
        {formatCurrency(item.subtotal)}
      </td>
      <td className="py-3 pl-2 w-10">
        <button
          onClick={() => onRemove(index)}
          className="p-1 text-red-400 hover:bg-red-500/20 rounded transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
};

// Quotation Form Component
const QuotationForm = ({ quotation, clients, vehicles, businessId, onSave, onCancel }) => {
  const isAutomotriz = businessId === "automotriz";
  // Plazos y defaults difieren por negocio
  const MONTH_OPTIONS = isAutomotriz ? [36, 48, 60, 72] : [3, 6, 9, 12, 18, 24];
  const DEFAULT_MONTHS = isAutomotriz ? 60 : 12;
  const DEFAULT_DOWN_PCT = isAutomotriz ? 20 : 30;

  const [formData, setFormData] = useState({
    quotation_type: 'normal', // 'normal' o 'credit'
    client_id: '',
    attention_to: '',
    attention_position: '',
    attention_email: '',
    cc_contacts: [],
    client_email: '',
    date: new Date().toISOString().split('T')[0],
    validity_days: 15,
    items: [],
    discount_percent: 0,
    // Campos para crédito
    down_payment_percent: DEFAULT_DOWN_PCT,
    payment_months: DEFAULT_MONTHS,
    notes: '',
    terms_conditions: '',
    seller_user_id: '',
    seller_name: '',
    seller_position: ''
  });
  const [loading, setLoading] = useState(false);
  const [showAddCC, setShowAddCC] = useState(false);
  const [newCC, setNewCC] = useState({ name: '', email: '', position: '' });
  const [sellers, setSellers] = useState([]);
  // Tasa anual (solo automotriz la carga del config; maquinaria queda fija en 18)
  const [annualRate, setAnnualRate] = useState(isAutomotriz ? 20 : 18);

  // Fetch sellers (active users) for the business
  useEffect(() => {
    if (!businessId) return;
    fetch(`${API}/api/users/sellers?business_id=${businessId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) setSellers(data.users || []);
      })
      .catch(err => console.error('Error fetching sellers:', err));
  }, [businessId]);

  // Fetch tasa anual del config del negocio (solo automotriz)
  useEffect(() => {
    if (!isAutomotriz) return;
    fetch(`${API}/api/config?business_id=automotriz`)
      .then(r => r.json())
      .then(data => {
        if (data?.success && data.config?.interest_rate) {
          setAnnualRate(parseFloat(data.config.interest_rate));
        }
      })
      .catch(err => console.error('Error fetching config:', err));
  }, [isAutomotriz]);

  useEffect(() => {
    if (quotation) {
      setFormData({
        quotation_type: quotation.quotation_type || 'normal',
        client_id: quotation.client_id || '',
        attention_to: quotation.attention_to || '',
        attention_position: quotation.attention_position || '',
        attention_email: quotation.attention_email || '',
        cc_contacts: quotation.cc_contacts || [],
        client_email: quotation.client_email || '',
        date: quotation.date ? new Date(quotation.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        validity_days: quotation.validity_days || 15,
        items: quotation.items || [],
        discount_percent: quotation.discount_percent || 0,
        down_payment_percent: quotation.down_payment_percent || 30,
        payment_months: quotation.payment_months || 12,
        notes: quotation.notes || '',
        terms_conditions: quotation.terms_conditions || '',
        seller_user_id: quotation.seller_user_id || '',
        seller_name: quotation.seller_name || '',
        seller_position: quotation.seller_position || ''
      });
    }
  }, [quotation]);

  // Calculate totals - usar el precio con IVA del catálogo / 1.16 para obtener precio sin IVA
  const subtotalSinIVA = formData.items.reduce((sum, item) => {
    const precioSinIVA = (item.unit_price || 0) / 1.16;
    return sum + ((item.quantity || 1) * precioSinIVA);
  }, 0);
  const discountAmount = subtotalSinIVA * (formData.discount_percent / 100);
  const subtotalConDescuento = subtotalSinIVA - discountAmount;
  const ivaAmount = subtotalConDescuento * 0.16;
  const total = subtotalConDescuento + ivaAmount;
  
  // Cálculos para crédito
  // - Automotriz: amortización compuesta a tasa anual del config (idéntico al catálogo público)
  // - Maquinaria: interés simple flat rate 18% (comportamiento previo)
  const ANNUAL_INTEREST_RATE = annualRate;
  const downPaymentAmount = total * (formData.down_payment_percent / 100);
  const financedAmount = total - downPaymentAmount;

  let monthlyPayment = 0;
  let totalInterest = 0;
  if (financedAmount > 0 && formData.payment_months > 0) {
    if (isAutomotriz) {
      const r = (ANNUAL_INTEREST_RATE / 100) / 12;
      monthlyPayment = r === 0
        ? financedAmount / formData.payment_months
        : (financedAmount * r * Math.pow(1 + r, formData.payment_months)) / (Math.pow(1 + r, formData.payment_months) - 1);
      totalInterest = (monthlyPayment * formData.payment_months) - financedAmount;
    } else {
      totalInterest = financedAmount * (ANNUAL_INTEREST_RATE / 100);
      monthlyPayment = (financedAmount + totalInterest) / formData.payment_months;
    }
  }

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        item_id: Date.now().toString(),
        code: '',
        vehicle_id: '',
        concept: '',
        unit: 'Pieza',
        quantity: 1,
        unit_price: 0,
        subtotal: 0
      }]
    }));
  };

  const updateItem = (index, updatedItem) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? updatedItem : item)
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const addCC = () => {
    if (newCC.name && newCC.email && formData.cc_contacts.length < 3) {
      setFormData(prev => ({
        ...prev,
        cc_contacts: [...prev.cc_contacts, { ...newCC }]
      }));
      setNewCC({ name: '', email: '', position: '' });
      setShowAddCC(false);
    }
  };

  const removeCC = (index) => {
    setFormData(prev => ({
      ...prev,
      cc_contacts: prev.cc_contacts.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.items.length === 0) {
      alert('Agrega al menos un producto a la cotización');
      return;
    }
    
    setLoading(true);
    try {
      const url = quotation?.id 
        ? `${API}/api/quotations/${quotation.id}`
        : `${API}/api/quotations`;
      
      const method = quotation?.id ? 'PUT' : 'POST';
      
      const payload = quotation?.id ? formData : {
        ...formData,
        business_id: businessId
      };
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (data.success) {
        onSave(data.quotation);
      }
    } catch (error) {
      console.error('Error saving quotation:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FileText className="w-5 h-5 text-orange-400" />
          {quotation?.id ? `Editar Cotización ${quotation.quotation_number}` : 'Nueva Cotización'}
        </h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tipo de Cotización */}
        <div className="p-4 bg-slate-900/50 rounded-lg border border-slate-700">
          <label className="block text-sm text-slate-400 mb-3">Tipo de Cotización</label>
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => setFormData({...formData, quotation_type: 'normal'})}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                formData.quotation_type === 'normal' 
                  ? 'border-blue-500 bg-blue-500/20 text-blue-400' 
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span className="font-medium">Contado</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData({...formData, quotation_type: 'credit'})}
              className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                formData.quotation_type === 'credit' 
                  ? 'border-orange-500 bg-orange-500/20 text-orange-400' 
                  : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-600'
              }`}
            >
              <Percent className="w-5 h-5" />
              <span className="font-medium">Crédito ({ANNUAL_INTEREST_RATE}% anual)</span>
            </button>
          </div>
          
          {/* Opciones de Crédito */}
          {formData.quotation_type === 'credit' && (
            <div className="mt-4 pt-4 border-t border-slate-700 space-y-4">
              {isAutomotriz ? (
                <>
                  {/* Enganche: slider 10-50% + campo % + campo $ (bidireccional) — idéntico al catálogo público */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="text-sm text-slate-400 inline-flex items-center gap-2">
                        <Percent className="w-3.5 h-3.5" /> Enganche
                      </label>
                      <span className="text-xs text-slate-500">10% – 50%</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="50"
                      step="1"
                      value={Math.min(50, Math.max(10, Math.round(formData.down_payment_percent || 0)))}
                      onChange={(e) => setFormData({...formData, down_payment_percent: parseInt(e.target.value)})}
                      className="w-full accent-orange-500"
                      data-testid="quote-down-slider"
                    />
                    <div className="grid grid-cols-2 gap-3 mt-3">
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Porcentaje</label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="90"
                            step="1"
                            value={formData.down_payment_percent}
                            onChange={(e) => {
                              const v = parseFloat(e.target.value);
                              setFormData({...formData, down_payment_percent: isNaN(v) ? 0 : v});
                            }}
                            className="w-full pl-3 pr-8 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                            data-testid="quote-down-pct-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-slate-500 mb-1">Cantidad</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
                          <input
                            type="number"
                            min="0"
                            step="1000"
                            value={Math.round(downPaymentAmount)}
                            onChange={(e) => {
                              const amount = parseFloat(e.target.value) || 0;
                              const pct = total > 0 ? (amount / total) * 100 : 0;
                              setFormData({...formData, down_payment_percent: Math.round(pct * 100) / 100});
                            }}
                            disabled={total <= 0}
                            className="w-full pl-8 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none disabled:opacity-50"
                            data-testid="quote-down-amount-input"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Plazo: botones 36/48/60/72 (idéntico al catálogo) */}
                  <div>
                    <label className="text-sm text-slate-400 mb-2 inline-flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5" /> Plazo
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {MONTH_OPTIONS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setFormData({...formData, payment_months: m})}
                          className={`py-2 rounded-lg font-medium text-sm transition-colors ${
                            m === formData.payment_months
                              ? 'bg-orange-500 text-white'
                              : 'bg-slate-900 text-slate-300 border border-slate-700 hover:border-orange-500/50'
                          }`}
                          data-testid={`quote-term-${m}`}
                        >
                          {m} meses
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Enganche (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="90"
                      value={formData.down_payment_percent}
                      onChange={(e) => setFormData({...formData, down_payment_percent: parseFloat(e.target.value) || 0})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-400 mb-1">Plazo (meses)</label>
                    <select
                      value={formData.payment_months}
                      onChange={(e) => setFormData({...formData, payment_months: parseInt(e.target.value)})}
                      className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-orange-500 focus:outline-none"
                    >
                      {MONTH_OPTIONS.map(m => (
                        <option key={m} value={m}>{m} meses</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
              
              {/* Resumen del Financiamiento */}
              {total > 0 && (
                <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                  <h4 className="text-orange-400 font-medium text-sm mb-2">Resumen del Financiamiento</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-slate-400 block">Enganche ({formData.down_payment_percent}%)</span>
                      <span className="text-white font-medium">{formatCurrency(downPaymentAmount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">A Financiar</span>
                      <span className="text-white font-medium">{formatCurrency(financedAmount)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Plazo</span>
                      <span className="text-white font-medium">{formData.payment_months} meses</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Pago Mensual</span>
                      <span className="text-orange-400 font-bold text-base">{formatCurrency(monthlyPayment)}</span>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {isAutomotriz
                      ? `* Tasa referencial ${ANNUAL_INTEREST_RATE}% anual · Amortización compuesta (mismo cálculo que el catálogo). Sujeto a aprobación crediticia.`
                      : `* Tasa de interés fija del ${ANNUAL_INTEREST_RATE}% anual`}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Cliente y Fecha */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Cliente</label>
            <select
              value={formData.client_id}
              onChange={(e) => {
                const clientId = e.target.value;
                const selectedClient = clients.find(c => c.id === clientId);
                const pc = selectedClient?.primary_contact || {};
                const addl = (selectedClient?.additional_contacts || []).map(c => ({
                  name: c.name || '',
                  position: c.position || '',
                  email: c.email || ''
                }));
                setFormData({
                  ...formData, 
                  client_id: clientId,
                  client_email: pc.email || selectedClient?.email || '',
                  attention_to: pc.name || '',
                  attention_position: pc.position || '',
                  attention_email: pc.email || '',
                  cc_contacts: addl,
                });
              }}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Público en General</option>
              {clients.map(c => (
                <option key={c.id} value={c.id}>{c.business_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Email del Cliente</label>
            <input
              type="email"
              value={formData.client_email}
              onChange={(e) => setFormData({...formData, client_email: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              placeholder="correo@empresa.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Fecha</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Vigencia (días)</label>
              <input
                type="number"
                min="1"
                value={formData.validity_days}
                onChange={(e) => setFormData({...formData, validity_days: parseInt(e.target.value) || 15})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Atención a - con cargo y email */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Atención a (AT'N)</label>
            <input
              type="text"
              value={formData.attention_to}
              onChange={(e) => setFormData({...formData, attention_to: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Nombre del contacto principal"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Cargo (AT'N)</label>
            <input
              type="text"
              value={formData.attention_position}
              onChange={(e) => setFormData({...formData, attention_position: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Ej: Director de Compras"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Email (AT'N)</label>
            <input
              type="email"
              value={formData.attention_email}
              onChange={(e) => setFormData({...formData, attention_email: e.target.value})}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
              placeholder="correo@contacto.com"
            />
          </div>
        </div>

        {/* CC Contacts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-slate-400">Con copia (C.C.P.)</label>
            {formData.cc_contacts.length < 3 && (
              <button
                type="button"
                onClick={() => setShowAddCC(true)}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                + Agregar
              </button>
            )}
          </div>
          {showAddCC && (
            <div className="flex gap-2 mb-2 flex-wrap">
              <input
                type="text"
                placeholder="Nombre"
                value={newCC.name}
                onChange={(e) => setNewCC({...newCC, name: e.target.value})}
                className="flex-1 min-w-[150px] px-3 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm"
              />
              <input
                type="text"
                placeholder="Cargo"
                value={newCC.position}
                onChange={(e) => setNewCC({...newCC, position: e.target.value})}
                className="flex-1 min-w-[150px] px-3 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm"
              />
              <input
                type="email"
                placeholder="Correo"
                value={newCC.email}
                onChange={(e) => setNewCC({...newCC, email: e.target.value})}
                className="flex-1 min-w-[180px] px-3 py-1.5 bg-slate-900 border border-slate-700 rounded text-white text-sm"
              />
              <button
                type="button"
                onClick={addCC}
                className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm"
              >
                Agregar
              </button>
              <button
                type="button"
                onClick={() => setShowAddCC(false)}
                className="px-3 py-1.5 text-slate-400 text-sm"
              >
                Cancelar
              </button>
            </div>
          )}
          {formData.cc_contacts.length > 0 && (
            <div className="space-y-1">
              {formData.cc_contacts.map((cc, i) => (
                <div key={i} className="px-3 py-2 bg-slate-700/50 rounded text-sm text-white flex items-center justify-between">
                  <div>
                    <span className="font-medium">{cc.name}</span>
                    {cc.position && <span className="text-slate-400 ml-2">({cc.position})</span>}
                    <span className="text-slate-400 ml-2">&lt;{cc.email}&gt;</span>
                  </div>
                  <button type="button" onClick={() => removeCC(i)} className="text-red-400 hover:text-red-300">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Items Table */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm text-slate-400 font-medium">Productos / Equipos</label>
            <button
              type="button"
              onClick={addItem}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Agregar línea
            </button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700 text-slate-400">
                  <th className="text-left py-2 pr-2 font-medium">Equipo</th>
                  <th className="text-left py-2 px-2 font-medium">Concepto</th>
                  <th className="text-center py-2 px-2 font-medium w-20">Cant.</th>
                  <th className="text-right py-2 px-2 font-medium w-32">P. Unit.</th>
                  <th className="text-right py-2 px-2 font-medium w-32">Subtotal</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((item, index) => (
                  <QuotationItemRow
                    key={item.item_id || index}
                    item={item}
                    index={index}
                    vehicles={vehicles}
                    onUpdate={updateItem}
                    onRemove={removeItem}
                  />
                ))}
              </tbody>
            </table>
          </div>
          
          {formData.items.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>No hay productos. Agrega una línea para comenzar.</p>
            </div>
          )}
        </div>

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-80 space-y-2">
            <div className="flex justify-between text-slate-400">
              <span>Subtotal (sin IVA):</span>
              <span className="text-white">{formatCurrency(subtotalSinIVA)}</span>
            </div>
            <div className="flex items-center justify-between text-slate-400">
              <div className="flex items-center gap-2">
                <span>Descuento:</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({...formData, discount_percent: parseFloat(e.target.value) || 0})}
                  className="w-16 px-2 py-1 bg-slate-900 border border-slate-700 rounded text-white text-sm text-center"
                />
                <span>%</span>
              </div>
              <span className="text-red-400">- {formatCurrency(discountAmount)}</span>
            </div>
            <div className="flex justify-between text-slate-400">
              <span>IVA (16%):</span>
              <span className="text-white">{formatCurrency(ivaAmount)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700">
              <span className="text-white">Total:</span>
              <span className="text-orange-400">{formatCurrency(total)}</span>
            </div>
            
            {/* Resumen de Crédito */}
            {formData.quotation_type === 'credit' && total > 0 && (
              <div className="mt-4 pt-3 border-t border-orange-500/30 space-y-2 text-sm">
                <div className="text-orange-400 font-medium">Plan de Crédito:</div>
                <div className="flex justify-between text-slate-400">
                  <span>Enganche ({formData.down_payment_percent}%):</span>
                  <span className="text-white">{formatCurrency(downPaymentAmount)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>A Financiar:</span>
                  <span className="text-white">{formatCurrency(financedAmount)}</span>
                </div>
                <div className="flex justify-between text-slate-400">
                  <span>Plazo:</span>
                  <span className="text-white">{formData.payment_months} meses</span>
                </div>
                <div className="flex justify-between font-bold pt-1 border-t border-slate-700">
                  <span className="text-white">Pago Mensual:</span>
                  <span className="text-orange-400">{formatCurrency(monthlyPayment)}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm text-slate-400 mb-1">Notas internas (no aparecen en PDF)</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({...formData, notes: e.target.value})}
            rows={2}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none resize-none"
            placeholder="Notas o comentarios internos..."
          />
        </div>

        {/* Seller / Signature Section */}
        <div className="bg-slate-700/30 rounded-lg p-4">
          <label className="block text-sm text-slate-400 mb-3 font-medium">
            Vendedor * <span className="text-xs text-slate-500">• Se usa su firma digital en el PDF</span>
          </label>
          <select
            value={formData.seller_user_id}
            onChange={(e) => {
              const userId = e.target.value;
              const u = sellers.find(s => s.id === userId);
              setFormData({
                ...formData,
                seller_user_id: userId,
                seller_name: u ? (u.full_name || u.username || '') : '',
                seller_position: u ? (u.position || '') : ''
              });
            }}
            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
            data-testid="quotation-seller-select"
          >
            <option value="">Seleccionar vendedor...</option>
            {sellers.map(u => (
              <option key={u.id} value={u.id}>
                {u.full_name || u.username} — {u.position || 'Sin cargo'}
                {u.signature_image ? ' ✍️' : ''}
              </option>
            ))}
          </select>
          {sellers.length === 0 && (
            <p className="text-xs text-amber-400 mt-1">
              No hay usuarios activos. Crea uno en la sección Usuarios.
            </p>
          )}
          {formData.seller_user_id && !sellers.find(s => s.id === formData.seller_user_id)?.signature_image && (
            <p className="text-xs text-amber-400 mt-1">
              Este usuario no tiene firma digital. Súbela en Usuarios para que aparezca en el PDF.
            </p>
          )}
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50 transition-colors"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {quotation?.id ? 'Guardar Cambios' : 'Crear Cotización'}
          </button>
        </div>
      </form>
    </div>
  );
};

// Quotation Card Component
const QuotationCard = ({ quotation, onEdit, onDelete, onDuplicate, onView, onDownloadPdf, onSendEmail, onAccept, onUploadInvoice, onDownloadInvoice, onSendInvoice, onDeleteInvoice, onDeliveryLetter }) => {
  const status = STATUS_CONFIG[quotation.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  const isCredit = quotation.quotation_type === 'credit';
  const isAccepted = quotation.status === 'accepted';
  const hasInvoice = !!quotation.invoice_number;
  const invoiceSent = !!quotation.invoice_sent_to_email;
  const deliveryFullySigned = !!quotation.delivery_letter?.signed_at;
  const deliveryPartial = !!quotation.delivery_letter?.created_at && !deliveryFullySigned;
  
  // Calcular estado de vigencia (solo relevante cuando NO está aceptada/rechazada)
  const expiryBadge = (() => {
    if (isAccepted || quotation.status === 'rejected') return null;
    const vu = quotation.valid_until;
    if (!vu) return null;
    try {
      const due = new Date(vu);
      if (isNaN(due.getTime())) return null;
      const now = new Date();
      // Normalizar a comparación por día
      due.setHours(23, 59, 59, 999);
      const msDiff = due.getTime() - now.getTime();
      const daysLeft = Math.ceil(msDiff / (1000 * 60 * 60 * 24));
      if (daysLeft < 0) {
        return { label: `VENCIDA hace ${Math.abs(daysLeft)}d`, color: 'bg-red-500/30 text-red-300 border border-red-500/40', testid: `expired-${quotation.id}` };
      }
      if (daysLeft <= 3) {
        return { label: daysLeft === 0 ? 'VENCE HOY' : `VENCE EN ${daysLeft}D`, color: 'bg-amber-500/30 text-amber-300 border border-amber-500/40', testid: `expiring-${quotation.id}` };
      }
      return null;
    } catch {
      return null;
    }
  })();

  // Visual emphasis when invoice is uploaded
  const cardClass = hasInvoice
    ? "bg-emerald-900/10 rounded-lg border border-emerald-600/40 p-4 hover:bg-emerald-900/20 transition-colors"
    : "bg-slate-800/30 rounded-lg border border-slate-700/50 p-4 hover:bg-slate-800/50 transition-colors";

  return (
    <div className={cardClass}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isCredit ? 'bg-purple-500/20' : hasInvoice ? 'bg-emerald-500/20' : 'bg-orange-500/20'}`}>
            {isCredit ? <Percent className="w-6 h-6 text-purple-400" /> : hasInvoice ? <Receipt className="w-6 h-6 text-emerald-400" /> : <FileText className="w-6 h-6 text-orange-400" />}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-semibold text-white">{quotation.quotation_number}</h4>
              {isCredit && (
                <span className="px-2 py-0.5 rounded text-xs bg-purple-500/30 text-purple-400">
                  Crédito
                </span>
              )}
              <span className={`px-2 py-0.5 rounded text-xs text-white flex items-center gap-1 ${status.color}`}>
                <StatusIcon className="w-3 h-3" />
                {status.label}
              </span>
              {expiryBadge && (
                <span
                  className={`px-2 py-0.5 rounded text-xs font-semibold flex items-center gap-1 ${expiryBadge.color}`}
                  title={`Vigencia: ${quotation.valid_until}`}
                  data-testid={expiryBadge.testid}
                >
                  <Clock className="w-3 h-3" />
                  {expiryBadge.label}
                </span>
              )}
              {hasInvoice && (
                <span
                  className="px-2 py-0.5 rounded text-xs bg-emerald-500/30 text-emerald-300 flex items-center gap-1"
                  title={`Factura ${quotation.invoice_number}`}
                >
                  <Receipt className="w-3 h-3" />
                  Facturada {quotation.invoice_number}
                </span>
              )}
              {invoiceSent && (
                <span className="px-2 py-0.5 rounded text-xs bg-blue-500/30 text-blue-300" title={`Factura enviada a ${quotation.invoice_sent_to_email}`}>
                  📧 Factura enviada
                </span>
              )}
              {deliveryFullySigned && (
                <span className="px-2 py-0.5 rounded text-xs bg-emerald-600/30 text-emerald-200 flex items-center gap-1" title="Acta de entrega firmada por ambas partes">
                  <FileSignature className="w-3 h-3" /> Acta firmada
                </span>
              )}
              {deliveryPartial && (
                <span className="px-2 py-0.5 rounded text-xs bg-amber-600/30 text-amber-200 flex items-center gap-1" title="Acta iniciada, pendiente de firmas">
                  <FileSignature className="w-3 h-3" /> Acta pendiente
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400">{quotation.client_name}</p>
            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500 flex-wrap">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(quotation.date)}
              </span>
              <span className="flex items-center gap-1">
                <Package className="w-3 h-3" />
                {quotation.items?.length || 0} items
              </span>
              {quotation.sent_to_email && (
                <span className="flex items-center gap-1 text-blue-400" title={`Enviado a ${quotation.sent_to_email}`}>
                  <Mail className="w-3 h-3" />
                  Cotización enviada
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="text-lg font-bold text-orange-400">{formatCurrency(quotation.total)}</p>
          {isCredit && quotation.monthly_payment > 0 && (
            <p className="text-xs text-purple-400">
              {quotation.payment_months} meses × {formatCurrency(quotation.monthly_payment)}
            </p>
          )}
          <div className="flex items-center gap-1 mt-2 flex-wrap justify-end">
            {/* Cotización actions */}
            <button
              onClick={() => onDownloadPdf(quotation)}
              className="p-1.5 text-green-400 hover:bg-green-500/20 rounded transition-colors"
              title="Descargar PDF Cotización"
              data-testid={`download-pdf-${quotation.id}`}
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={() => onSendEmail(quotation)}
              className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
              title="Enviar cotización por correo"
              data-testid={`send-email-${quotation.id}`}
            >
              <Send className="w-4 h-4" />
            </button>
            {!isAccepted && (
              <button
                onClick={() => onAccept(quotation)}
                className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded transition-colors"
                title="Marcar como Aceptada"
                data-testid={`accept-quotation-${quotation.id}`}
              >
                <CheckCircle className="w-4 h-4" />
              </button>
            )}
            {/* Invoice actions */}
            <div className="w-px h-5 bg-slate-600 mx-0.5" />
            {!hasInvoice ? (
              <button
                onClick={() => onUploadInvoice(quotation)}
                className="p-1.5 text-amber-400 hover:bg-amber-500/20 rounded transition-colors"
                title="Subir Factura y XML"
                data-testid={`upload-invoice-${quotation.id}`}
              >
                <FileUp className="w-4 h-4" />
              </button>
            ) : (
              <>
                <button
                  onClick={() => onUploadInvoice(quotation)}
                  className="p-1.5 text-amber-400 hover:bg-amber-500/20 rounded transition-colors"
                  title="Reemplazar Factura y XML"
                  data-testid={`replace-invoice-${quotation.id}`}
                >
                  <FileUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDownloadInvoice(quotation, 'pdf')}
                  className="p-1.5 text-emerald-400 hover:bg-emerald-500/20 rounded transition-colors"
                  title={`Descargar Factura PDF (${quotation.invoice_number})`}
                  data-testid={`download-invoice-pdf-${quotation.id}`}
                >
                  <Receipt className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDownloadInvoice(quotation, 'xml')}
                  className="p-1.5 text-emerald-300 hover:bg-emerald-500/20 rounded transition-colors text-xs font-bold"
                  title={`Descargar XML (CFDI)`}
                  data-testid={`download-invoice-xml-${quotation.id}`}
                >
                  XML
                </button>
                <button
                  onClick={() => onSendInvoice(quotation)}
                  className="p-1.5 text-blue-300 hover:bg-blue-500/20 rounded transition-colors"
                  title="Enviar Factura por correo"
                  data-testid={`send-invoice-${quotation.id}`}
                >
                  <Mail className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDeleteInvoice(quotation)}
                  className="p-1.5 text-red-300 hover:bg-red-500/20 rounded transition-colors"
                  title="Eliminar factura"
                  data-testid={`delete-invoice-${quotation.id}`}
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </>
            )}
            {/* Delivery letter button */}
            <button
              onClick={() => onDeliveryLetter(quotation)}
              className={`p-1.5 rounded transition-colors ${
                deliveryFullySigned
                  ? 'text-emerald-400 hover:bg-emerald-500/20'
                  : deliveryPartial
                    ? 'text-amber-400 hover:bg-amber-500/20'
                    : 'text-slate-400 hover:bg-slate-700'
              }`}
              title={deliveryFullySigned ? 'Ver Acta de Entrega firmada' : (deliveryPartial ? 'Continuar Acta de Entrega' : 'Crear Acta de Entrega')}
              data-testid={`delivery-letter-${quotation.id}`}
            >
              <FileSignature className="w-4 h-4" />
            </button>
            <div className="w-px h-5 bg-slate-600 mx-0.5" />
            <button
              onClick={() => onView(quotation)}
              className="p-1.5 text-slate-400 hover:bg-slate-700 rounded transition-colors"
              title="Ver"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => onEdit(quotation)}
              className="p-1.5 text-slate-400 hover:bg-slate-700 rounded transition-colors"
              title="Editar"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDuplicate(quotation)}
              className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
              title="Duplicar"
            >
              <Copy className="w-4 h-4" />
            </button>
            <button
              onClick={() => onDelete(quotation)}
              className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
              title="Eliminar"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Quotations Tab Component
export default function QuotationsTab({ businessId, initialClient }) {
  const { businessId: contextBusinessId } = useBusiness();
  const actualBusinessId = businessId || contextBusinessId;
  
  // State for quotation type toggle (only for maquinaria)
  const [quotationType, setQuotationType] = useState('venta'); // 'venta' or 'renta'
  
  const [quotations, setQuotations] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState(null);

  const fetchData = useCallback(async () => {
    if (!actualBusinessId) return;
    setLoading(true);
    try {
      const [quotRes, clientRes, vehicleRes, statsRes] = await Promise.all([
        fetch(`${API}/api/quotations?business_id=${actualBusinessId}`),
        fetch(`${API}/api/clients`),  // Cargar TODOS los clientes
        fetch(`${API}/api/vehicles?business_id=${actualBusinessId}`),
        fetch(`${API}/api/quotations/stats/${actualBusinessId}`)
      ]);
      
      const [quotData, clientData, vehicleData, statsData] = await Promise.all([
        quotRes.json(),
        clientRes.json(),
        vehicleRes.json(),
        statsRes.json()
      ]);
      
      if (quotData.success) setQuotations(quotData.quotations || []);
      if (clientData.success) setClients(clientData.clients || []);
      if (vehicleData.success) setVehicles(vehicleData.vehicles || []);
      if (statsData.success) setStats(statsData.stats);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [actualBusinessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Open form with initial client if provided
  useEffect(() => {
    if (initialClient) {
      setEditingQuotation({ client_id: initialClient.id });
      setShowForm(true);
    }
  }, [initialClient]);

  const handleSave = (quotation) => {
    if (editingQuotation?.id) {
      setQuotations(prev => prev.map(q => q.id === quotation.id ? quotation : q));
    } else {
      setQuotations(prev => [quotation, ...prev]);
    }
    setShowForm(false);
    setEditingQuotation(null);
    fetchData(); // Refresh stats
  };

  const handleDelete = async (quotation) => {
    if (!window.confirm(`¿Eliminar cotización ${quotation.quotation_number}?`)) return;
    
    try {
      const res = await fetch(`${API}/api/quotations/${quotation.id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        setQuotations(prev => prev.filter(q => q.id !== quotation.id));
        fetchData(); // Refresh stats
      }
    } catch (error) {
      console.error('Error deleting quotation:', error);
    }
  };

  const handleDuplicate = async (quotation) => {
    try {
      const res = await fetch(`${API}/api/quotations/${quotation.id}/duplicate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setQuotations(prev => [data.quotation, ...prev]);
      }
    } catch (error) {
      console.error('Error duplicating quotation:', error);
    }
  };

  const handleDownloadPdf = async (quotation) => {
    try {
      toast.info("Generando PDF...");
      const response = await fetch(`${API}/api/quotations/${quotation.id}/pdf`);
      if (!response.ok) throw new Error("Error al generar PDF");
      const blob = await response.blob();
      const filename = `COTIZACION-${quotation.quotation_number || 'SN'}.pdf`;
      
      // Usar File System Access API — abre el cuadro nativo "Guardar como..."
      if (window.showSaveFilePicker) {
        try {
          const handle = await window.showSaveFilePicker({
            suggestedName: filename,
            types: [{ description: 'PDF', accept: { 'application/pdf': ['.pdf'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          toast.success("PDF guardado correctamente");
          return;
        } catch (e) {
          if (e.name === 'AbortError') return; // Usuario canceló
        }
      }
      
      // Fallback
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success("PDF descargado");
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error("Error al descargar PDF");
    }
  };

  // ==================== SEND EMAIL ====================
  const [sendingEmail, setSendingEmail] = useState(null); // quotation with override email
  const [sendingInProgress, setSendingInProgress] = useState(false);

  const handleSendEmail = (quotation) => {
    const ccList = (quotation.cc_contacts || [])
      .map(c => (c.email || '').trim().toLowerCase())
      .filter(Boolean);
    setSendingEmail({
      ...quotation,
      _client_email: (quotation.client_email || '').toLowerCase(),
      _cc_emails: ccList
    });
  };

  const confirmSendEmail = async () => {
    if (!sendingEmail) return;
    const quotation = sendingEmail;
    const overrideEmail = (sendingEmail._client_email || '').trim();
    if (!overrideEmail) {
      toast.error("El cliente no tiene correo electrónico. Agrégalo en Clientes.");
      return;
    }
    setSendingInProgress(true);
    try {
      toast.info(`Enviando Cotización ${quotation.quotation_number}...`);
      const response = await fetch(`${API}/api/quotations/${quotation.id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override_email: overrideEmail })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Correo enviado a ${data.sent_to}` + (data.cc?.length ? ` (C.C. ${data.cc.length})` : ''));
        setSendingEmail(null);
        fetchData();
      } else {
        toast.error(data.error || "Error al enviar correo");
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error("Error: " + error.message);
    } finally {
      setSendingInProgress(false);
    }
  };

  // ==================== ACCEPT QUOTATION ====================
  const handleAccept = async (quotation) => {
    if (!window.confirm(
      `¿Marcar ${quotation.quotation_number} como ACEPTADA?\n\nSe generará automáticamente un borrador de Orden de Compra con los mismos vehículos.`
    )) return;
    try {
      const response = await fetch(`${API}/api/quotations/${quotation.id}/accept`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        if (data.po_already_existed) {
          toast.success(`Aceptada. OC ya existente: ${data.purchase_order?.order_number || ''}`);
        } else {
          toast.success(`Aceptada. Borrador creado: ${data.purchase_order?.order_number || 'OC-?'}`);
        }
        fetchData();
      } else {
        toast.error(data.error || "Error");
      }
    } catch (error) {
      toast.error("Error: " + error.message);
    }
  };

  // ==================== INVOICE (FACTURA + XML) ====================
  const [uploadingInvoice, setUploadingInvoice] = useState(null); // {quotation, pdfFile, xmlFile, invoiceNumber}
  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [invoiceSendingInProgress, setInvoiceSendingInProgress] = useState(false);

  // Delivery letter modal
  const [deliveryLetterFor, setDeliveryLetterFor] = useState(null); // quotation or null

  const handleOpenDeliveryLetter = (quotation) => {
    setDeliveryLetterFor(quotation);
  };

  const handleUploadInvoice = (quotation) => {
    setUploadingInvoice({ quotation, pdfFile: null, xmlFile: null, invoiceNumber: '' });
  };

  const confirmUploadInvoice = async () => {
    if (!uploadingInvoice) return;
    const { quotation, pdfFile, xmlFile, invoiceNumber } = uploadingInvoice;
    if (!pdfFile || !xmlFile) {
      toast.error("Selecciona ambos archivos (PDF y XML)");
      return;
    }
    setInvoiceUploading(true);
    try {
      const fd = new FormData();
      fd.append('invoice_pdf', pdfFile);
      fd.append('invoice_xml', xmlFile);
      fd.append('invoice_number', invoiceNumber || '');
      const res = await fetch(`${API}/api/quotations/${quotation.id}/upload-invoice`, {
        method: 'POST',
        body: fd
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Factura ${data.invoice_number} subida correctamente`);
        setUploadingInvoice(null);
        fetchData();
      } else {
        toast.error(data.detail || data.error || "Error al subir factura");
      }
    } catch (error) {
      toast.error("Error: " + error.message);
    } finally {
      setInvoiceUploading(false);
    }
  };

  const handleDownloadInvoice = async (quotation, fileType) => {
    try {
      const res = await fetch(`${API}/api/quotations/${quotation.id}/invoice/download/${fileType}`);
      if (!res.ok) throw new Error("Error al descargar");
      const blob = await res.blob();
      const filename = `FACTURA_${quotation.invoice_number || quotation.quotation_number}.${fileType}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${fileType.toUpperCase()} descargado`);
    } catch (error) {
      toast.error("Error al descargar factura");
    }
  };

  const handleSendInvoice = (quotation) => {
    const ccList = (quotation.cc_contacts || [])
      .map(c => (c.email || '').trim().toLowerCase())
      .filter(Boolean);
    setSendingInvoice({
      ...quotation,
      _client_email: (quotation.client_email || '').toLowerCase(),
      _cc_emails: ccList
    });
  };

  const confirmSendInvoice = async () => {
    if (!sendingInvoice) return;
    const q = sendingInvoice;
    const overrideEmail = (sendingInvoice._client_email || '').trim();
    if (!overrideEmail) {
      toast.error("Falta correo del cliente.");
      return;
    }
    setInvoiceSendingInProgress(true);
    try {
      toast.info(`Enviando Factura ${q.invoice_number}...`);
      const res = await fetch(`${API}/api/quotations/${q.id}/send-invoice-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override_email: overrideEmail })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Factura enviada a ${data.sent_to}` + (data.cc?.length ? ` (C.C. ${data.cc.length})` : ''));
        setSendingInvoice(null);
        fetchData();
      } else {
        toast.error(data.error || "Error al enviar factura");
      }
    } catch (error) {
      toast.error("Error: " + error.message);
    } finally {
      setInvoiceSendingInProgress(false);
    }
  };

  const handleDeleteInvoice = async (quotation) => {
    if (!window.confirm(`¿Eliminar la factura ${quotation.invoice_number} de esta cotización?`)) return;
    try {
      const res = await fetch(`${API}/api/quotations/${quotation.id}/invoice`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success("Factura eliminada");
        fetchData();
      } else {
        toast.error(data.error || "Error");
      }
    } catch (error) {
      toast.error("Error: " + error.message);
    }
  };

  const filteredQuotations = quotations.filter(q => {
    const matchesSearch = 
      q.quotation_number?.toLowerCase().includes(search.toLowerCase()) ||
      q.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      (q.invoice_number || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="quotations-tab">
      {/* Header with Type Toggle for Maquinaria */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FileText className="w-7 h-7 text-orange-400" />
            Cotizaciones
          </h2>
          {/* Type Toggle - Only for Maquinaria */}
          {actualBusinessId === "maquinaria" && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => setQuotationType('venta')}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  quotationType === 'venta'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <DollarSign className="w-4 h-4" />
                Venta
              </button>
              <button
                onClick={() => setQuotationType('renta')}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                  quotationType === 'renta'
                    ? 'bg-orange-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <Truck className="w-4 h-4" />
                Renta
              </button>
            </div>
          )}
          {quotationType === 'venta' && (
            <p className="text-slate-400 mt-1">
              {quotations.length} cotización{quotations.length !== 1 ? 'es' : ''} de venta
            </p>
          )}
        </div>
        {quotationType === 'venta' && (
          <button
            onClick={() => { setEditingQuotation(null); setShowForm(true); }}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 transition-colors"
            data-testid="add-quotation-btn"
          >
            <Plus className="w-5 h-5" />
            Nueva Cotización
          </button>
        )}
      </div>

      {/* If Renta type is selected, show RentalQuotationsTab */}
      {quotationType === 'renta' && actualBusinessId === "maquinaria" ? (
        <RentalQuotationsTab />
      ) : (
      <>
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Borradores', value: stats.draft, color: 'text-slate-400' },
            { label: 'Enviadas', value: stats.sent, color: 'text-blue-400' },
            { label: 'Aceptadas', value: stats.accepted, color: 'text-green-400' },
            { label: 'Rechazadas', value: stats.rejected, color: 'text-red-400' },
            { label: 'Total Aceptado', value: formatCurrency(stats.total_value_accepted), color: 'text-orange-400', large: true }
          ].map((stat, i) => (
            <div key={i} className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
              <p className="text-xs text-slate-500 mb-1">{stat.label}</p>
              <p className={`text-xl font-bold ${stat.color}`}>{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Search & Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por número o cliente..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-orange-500 focus:outline-none"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white focus:border-orange-500 focus:outline-none"
        >
          <option value="">Todos los estados</option>
          <option value="draft">Borradores</option>
          <option value="sent">Enviadas</option>
          <option value="accepted">Aceptadas</option>
          <option value="rejected">Rechazadas</option>
          <option value="expired">Expiradas</option>
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <QuotationForm
          quotation={editingQuotation}
          clients={clients}
          vehicles={vehicles}
          businessId={businessId}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingQuotation(null); }}
        />
      )}

      {/* Quotations List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
        </div>
      ) : filteredQuotations.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{search || statusFilter ? 'No se encontraron cotizaciones' : 'No hay cotizaciones'}</p>
          {!search && !statusFilter && (
            <button
              onClick={() => setShowForm(true)}
              className="mt-4 text-orange-400 hover:text-orange-300"
            >
              Crear primera cotización
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredQuotations.map(quotation => (
            <QuotationCard
              key={quotation.id}
              quotation={quotation}
              onEdit={(q) => { setEditingQuotation(q); setShowForm(true); }}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onDownloadPdf={handleDownloadPdf}
              onView={(q) => handleDownloadPdf(q)} // View also downloads PDF
              onSendEmail={handleSendEmail}
              onAccept={handleAccept}
              onUploadInvoice={handleUploadInvoice}
              onDownloadInvoice={handleDownloadInvoice}
              onSendInvoice={handleSendInvoice}
              onDeleteInvoice={handleDeleteInvoice}
              onDeliveryLetter={handleOpenDeliveryLetter}
            />
          ))}
        </div>
      )}

      {/* SEND EMAIL MODAL (Cotización) */}
      <EmailEditModal
        open={!!sendingEmail}
        onClose={() => setSendingEmail(null)}
        onSent={() => fetchData()}
        title={sendingEmail ? `Enviar Cotización ${sendingEmail.quotation_number}` : 'Enviar Cotización'}
        sendEndpoint={sendingEmail ? `/api/quotations/${sendingEmail.id}/send-email` : ''}
        initialTo={sendingEmail?._client_email || sendingEmail?.client_email || ''}
        confirmLabel="Enviar Cotización"
        helperText="Se adjuntará el PDF de la cotización. Puedes editar destinatario, asunto y cuerpo."
      />

      {/* UPLOAD INVOICE MODAL */}
      {uploadingInvoice && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => !invoiceUploading && setUploadingInvoice(null)}>
          <div className="bg-slate-800 rounded-xl border border-emerald-600/40 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <FileUp className="w-5 h-5 text-emerald-400" />
              {uploadingInvoice.quotation.invoice_number ? 'Reemplazar Factura y XML' : 'Subir Factura y XML'}
            </h3>
            <div className="space-y-3 text-sm mb-5">
              {uploadingInvoice.quotation.invoice_number && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200">
                  ⚠️ Ya existe la factura <strong>{uploadingInvoice.quotation.invoice_number}</strong>. Al subir nuevos archivos, los anteriores se reemplazarán y el Acta de Entrega se re-sincronizará automáticamente.
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-400">Cotización:</span>
                <span className="text-white font-medium">{uploadingInvoice.quotation.quotation_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Cliente:</span>
                <span className="text-white">{uploadingInvoice.quotation.client_name}</span>
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Número de Factura (opcional)</label>
                <input
                  type="text"
                  value={uploadingInvoice.invoiceNumber}
                  onChange={(e) => setUploadingInvoice(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                  placeholder="Se extraerá del XML si lo dejas vacío"
                  className="w-full bg-slate-900 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none"
                  data-testid="invoice-number-input"
                />
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Archivo PDF *</label>
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => setUploadingInvoice(prev => ({ ...prev, pdfFile: e.target.files[0] }))}
                  className="w-full text-slate-300 text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-emerald-500/20 file:text-emerald-300 file:font-medium hover:file:bg-emerald-500/30 file:cursor-pointer"
                  data-testid="invoice-pdf-input"
                />
                {uploadingInvoice.pdfFile && (
                  <p className="text-xs text-emerald-400 mt-1">✓ {uploadingInvoice.pdfFile.name}</p>
                )}
              </div>
              <div>
                <label className="block text-slate-400 mb-1">Archivo XML (CFDI) *</label>
                <input
                  type="file"
                  accept=".xml,application/xml,text/xml"
                  onChange={(e) => setUploadingInvoice(prev => ({ ...prev, xmlFile: e.target.files[0] }))}
                  className="w-full text-slate-300 text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-emerald-500/20 file:text-emerald-300 file:font-medium hover:file:bg-emerald-500/30 file:cursor-pointer"
                  data-testid="invoice-xml-input"
                />
                {uploadingInvoice.xmlFile && (
                  <p className="text-xs text-emerald-400 mt-1">✓ {uploadingInvoice.xmlFile.name}</p>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setUploadingInvoice(null)}
                disabled={invoiceUploading}
                className="flex-1 py-2.5 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button
                onClick={confirmUploadInvoice}
                disabled={invoiceUploading || !uploadingInvoice.pdfFile || !uploadingInvoice.xmlFile}
                className="flex-1 py-2.5 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="confirm-upload-invoice-btn"
              >
                {invoiceUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
                {invoiceUploading ? 'Subiendo...' : 'Subir'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* SEND INVOICE MODAL (Factura) */}
      <EmailEditModal
        open={!!sendingInvoice}
        onClose={() => setSendingInvoice(null)}
        onSent={() => fetchData()}
        title={sendingInvoice ? `Enviar Factura ${sendingInvoice.invoice_number}` : 'Enviar Factura'}
        sendEndpoint={sendingInvoice ? `/api/quotations/${sendingInvoice.id}/send-invoice-email` : ''}
        initialTo={sendingInvoice?._client_email || sendingInvoice?.client_email || ''}
        confirmLabel="Enviar Factura"
        helperText="Se adjuntarán ambos archivos: PDF y XML (CFDI). Puedes editar destinatario, asunto y cuerpo."
      />

      {/* DELIVERY LETTER MODAL */}
      {deliveryLetterFor && (
        <DeliveryLetterModal
          quotationId={deliveryLetterFor.id}
          onClose={() => setDeliveryLetterFor(null)}
          onSaved={() => fetchData()}
        />
      )}
      </>
      )}
    </div>
  );
}
